// src/components/riskassessment/PortfolioAllocationChart.js
//
// 7-vault allocation pie + per-vault breakdown bars.
// Source of truth: results.vaultAllocation (new shape from cryptoCalculationUtils.js)
// Falls back to legacy `allocation.vaults.breakdown` if vaultAllocation isn't passed.

import React from "react";

/* ─── Vault catalog (single source of truth for labels, colors, risk) ──── */
const VAULTS = [
  { key: "leoBTC",   name: "Leo BTC Bull Run", risk: "3/10", color: "#22c55e", desc: "Long only · BTC · sits in cash during chop" },
  { key: "leoETH",   name: "Leo ETH",          risk: "4/10", color: "#10b981", desc: "Long only · ETH momentum, no leverage" },
  { key: "gemBTC",   name: "Gemini BTC",       risk: "5/10", color: "#3b82f6", desc: "Long/short · BTC · 28/28 recovery record" },
  { key: "gemini",   name: "Gemini",           risk: "5/10", color: "#6366f1", desc: "Long/short · BTC/ETH/SOL 33/33/33 split" },
  { key: "scorpioV", name: "Scorpio V",        risk: "7/10", color: "#f59e0b", desc: "Long/short · BTC · up to 12x leverage" },
  { key: "gemETH",   name: "Gemini ETH",       risk: "8/10", color: "#ef4444", desc: "Long/short · ETH · highest returns + drawdowns" },
  { key: "gemSOL",   name: "Gemini SOL",       risk: "8/10", color: "#dc2626", desc: "Long/short directional · SOL · most volatile" },
  { key: "cash",     name: "Cash / Stables",   risk: "—",    color: "#a1a1aa", desc: "Buffer for psychological safety + rebalancing" },
];

const PortfolioAllocationChart = ({ allocation, vaultAllocation }) => {
  // Prefer the new 7-vault shape. Fall back to legacy 3-bucket if needed.
  let perVault;
  if (vaultAllocation) {
    perVault = vaultAllocation;
  } else if (allocation?.vaults?.breakdown) {
    // Legacy fallback: distribute the 3-bucket numbers across the 7 vaults
    // proportionally — not perfect but better than crashing.
    const v = allocation.vaults;
    const leoTotal = (v.total * v.breakdown.leo) / 100;
    const gemTotal = (v.total * v.breakdown.gemini) / 100;
    const scoTotal = (v.total * v.breakdown.scorpio) / 100;
    perVault = {
      leoBTC: leoTotal * 0.6,
      leoETH: leoTotal * 0.4,
      gemBTC: gemTotal * 0.4,
      gemini: gemTotal * 0.3,
      gemETH: gemTotal * 0.2,
      gemSOL: gemTotal * 0.1,
      scorpioV: scoTotal,
      cash: allocation.stablecoins || 0,
    };
  } else {
    perVault = { leoBTC: 0, leoETH: 0, gemBTC: 0, gemini: 0, scorpioV: 0, gemETH: 0, gemSOL: 0, cash: 100 };
  }

  // Build segments — only include vaults with > 0 allocation
  const segments = VAULTS
    .map((v) => ({ ...v, value: Math.round((perVault[v.key] || 0) * 10) / 10 }))
    .filter((s) => s.value > 0);

  /* ─── SVG pie geometry ──────────────────────────────────────────────── */
  const size = 280;
  const radius = size / 2;
  const center = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;

  let startAngle = 0;
  const paths = segments.map((seg) => {
    const pct = seg.value / total;
    const angle = pct * 360;
    const endAngle = startAngle + angle;
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);
    const largeArc = angle > 180 ? 1 : 0;
    const path = `M ${center},${center} L ${x1},${y1} A ${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z`;

    const labelRad = ((startAngle + angle / 2 - 90) * Math.PI) / 180;
    const labelDist = radius * 0.7;
    const labelX = center + labelDist * Math.cos(labelRad);
    const labelY = center + labelDist * Math.sin(labelRad);

    startAngle = endAngle;
    return { ...seg, path, labelX, labelY, showLabel: pct > 0.04 };
  });

  return (
    <div className="mb-12">
      <h3 className="text-xl font-semibold text-arc-ink mb-4">
        Recommended Vault Allocation
      </h3>

      <div className="flex flex-col md:flex-row items-center justify-center gap-8">
        {/* Pie chart */}
        <div className="relative">
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {paths.map((seg, i) => (
              <path
                key={i}
                d={seg.path}
                fill={seg.color}
                stroke="#0a0a0b"
                strokeWidth="2"
              />
            ))}
            {paths.map((seg, i) =>
              seg.showLabel ? (
                <text
                  key={`l-${i}`}
                  x={seg.labelX}
                  y={seg.labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#fafafa"
                  fontSize="11"
                  fontWeight="600"
                >
                  {seg.value}%
                </text>
              ) : null
            )}
          </svg>
        </div>

        {/* Legend */}
        <div className="md:ml-4">
          <h4 className="eyebrow mb-3">Allocation Breakdown</h4>
          <div className="space-y-2">
            {VAULTS.map((v) => {
              const pct = perVault[v.key] || 0;
              return (
                <div key={v.key} className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ background: v.color, opacity: pct > 0 ? 1 : 0.3 }}
                  />
                  <span className={`text-sm ${pct > 0 ? "text-arc-ink" : "text-arc-dim"} flex-1`}>
                    {v.name}
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-arc-dim w-10">
                    {v.risk}
                  </span>
                  <span className="text-sm num text-arc-ink w-12 text-right">
                    {Math.round(pct * 10) / 10}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per-vault bars */}
      <div className="mt-8 bg-white/[0.02] border border-arc-line rounded-2xl p-5">
        <h4 className="eyebrow mb-4">Vault-by-Vault Breakdown</h4>
        <div className="space-y-3">
          {VAULTS.map((v) => {
            const pct = perVault[v.key] || 0;
            return (
              <div key={v.key}>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-sm text-arc-ink">
                    {v.name}{" "}
                    <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-arc-dim ml-1">
                      {v.risk}
                    </span>
                  </span>
                  <span className="text-sm num text-arc-ink">{Math.round(pct * 10) / 10}%</span>
                </div>
                <div className="w-full bg-white/[0.04] rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: v.color }}
                  />
                </div>
                <p className="text-xs text-arc-dim mt-1">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PortfolioAllocationChart;
